/**
 * orderUtils.ts — shared helpers for order data.
 *
 * Ported from:
 *   frontend-bk/erp/src/lib/order-category.js  (getOrderCategory)
 *   frontend-bk/erp/src/hooks/useOrderFilters.js (removeAccents)
 *
 * Plus adapter helpers required by Plan 3 Task 4.1 errata block
 * (assignedStaffFromOrder, selectedConceptOf) bridging the BE's
 * discrete staff fields to component props.
 */

// ══════════════════════════════════════════════════
// ACCENT STRIPPING
// ══════════════════════════════════════════════════

/**
 * Strip Vietnamese diacritics for fuzzy search comparisons.
 * Same logic as useOrderFilters.js removeAccents().
 */
export function removeAccents(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
}

// ══════════════════════════════════════════════════
// NGUỒN KHÁCH DO MÁY ĐÓNG DẤU
// ══════════════════════════════════════════════════

/**
 * Nhãn cho những giá trị `orders.lead_source` do MÁY tự đóng dấu — không phải
 * lựa chọn ai đó bấm trên màn tạo đơn.
 *
 * Vì sao tách riêng: danh sách nguồn ở màn tạo đơn vừa là NHÃN vừa là LỰA
 * CHỌN. Nhét mấy giá trị này vào đó là mọc thêm nút cho sale bấm tay, trong
 * khi chúng chỉ hợp lệ khi máy tự ghi.
 *
 * `public_booking` là khách tự vào trang đặt lịch mà KHÔNG qua link chiến
 * dịch nào (`orderLeadSource` bên máy chủ). Không có dòng này thì màn đơn
 * hàng, phễu nguồn khách và báo cáo mã giảm giá đều hiện trần chữ
 * "public_booking" cho người dùng đọc — CEO chốt 03/09/2026 phải đọc ra
 * tiếng người.
 */
export const MACHINE_LEAD_SOURCE_LABEL: Record<string, string> = {
  public_booking: "Khách tự tìm đến (web)",
  // Đơn KOL Marketing đặt qua lệnh buổi chụp (hợp đồng Marketing buoi-chup-v1).
  marketing_kol: "KOL do Marketing đặt",
};

/** Nhãn hiển thị của `orders.lead_source`; giá trị lạ giữ nguyên. */
export function labelOrderLeadSource(raw: string): string {
  return MACHINE_LEAD_SOURCE_LABEL[raw.toLowerCase()] ?? raw;
}

// ══════════════════════════════════════════════════
// CATEGORY DERIVATION
// ══════════════════════════════════════════════════

/**
 * Map productId prefix → category.
 * Derived from PRODUCT_CATEGORIES in data/constants.js — keep in sync.
 */
const PRODUCT_ID_PREFIX_MAP: Record<string, string> = {
  pf_: "profile",
  at_: "idphoto",
  cv_: "idphoto",
  dn_: "corporate",
};

export const VALID_CATEGORIES = ["profile", "idphoto", "corporate", "other"] as const;
export type OrderCategory = (typeof VALID_CATEGORIES)[number];

export interface OrderForCategory {
  category?: string | null;
  productId?: string | null;
  product?: string | null;
  productName?: string | null;
}

/**
 * Derives the product category for an order.
 *
 * Resolution order:
 * 1. order.category if present and valid
 * 2. order.productId prefix mapping
 * 3. order.product / order.productName keyword matching
 * 4. Fallback → 'other'
 *
 * Exact port of frontend-bk/erp/src/lib/order-category.js getOrderCategory().
 */
export function getOrderCategory(order: OrderForCategory): string {
  if (!order) return "other";

  // 1. Explicit field — trust if it's a recognised value
  const explicit = order.category;
  if (explicit && (VALID_CATEGORIES as readonly string[]).includes(explicit))
    return explicit;
  // Accept unknown explicit values as-is so they won't be hidden
  if (explicit && explicit !== "other") return explicit;

  // 2. Derive from productId prefix
  const pid = (order.productId ?? "").toLowerCase();
  if (pid) {
    for (const [prefix, cat] of Object.entries(PRODUCT_ID_PREFIX_MAP)) {
      if (pid.startsWith(prefix)) return cat;
    }
    if (pid.includes("profile")) return "profile";
    if (pid.includes("corporate") || pid.includes("doanh_nghiep"))
      return "corporate";
    if (pid.includes("idphoto") || pid.includes("anh_the")) return "idphoto";
  }

  // 3. Derive from product / productName keyword matching
  const name = ((order.product ?? order.productName) ?? "").toLowerCase();
  if (name) {
    if (
      name.includes("ảnh thẻ") ||
      name.includes("anh the") ||
      name.includes("cv ") ||
      name.includes("cv_") ||
      name.startsWith("cv")
    )
      return "idphoto";
    if (
      name.includes("doanh nghiệp") ||
      name.includes("doanh nghiep") ||
      name.includes("corporate") ||
      name.includes("enterprise")
    )
      return "corporate";
    if (
      name.includes("profile") ||
      name.includes("pf_") ||
      name.includes("co-founder") ||
      name.includes("cofounder")
    )
      return "profile";
  }

  // 4. Fallback
  return "other";
}

// ══════════════════════════════════════════════════
// PAYMENT HELPERS
// ══════════════════════════════════════════════════

/**
 * Calculate how much a customer still owes (total - paid), floored at 0.
 */
export function remainingPayment(total: number, paid: number): number {
  return Math.max(0, total - paid);
}

// ══════════════════════════════════════════════════
// PRODUCT NAME SHORTENING
// ══════════════════════════════════════════════════

/**
 * Shorten a product name for compact display (e.g. in table cells).
 * Removes common prefix words ("Ảnh", "Chụp ảnh") and trims whitespace.
 */
export function shortProductName(name: string): string {
  return name
    .replace(/^(chụp ảnh |ảnh )/i, "")
    .trim();
}

// ══════════════════════════════════════════════════
// ADAPTER HELPERS (C1 errata — Plan 3 Task 4.1)
// ══════════════════════════════════════════════════

/**
 * AssignedStaffMap is the component-friendly shape used by OrderStaffAssign
 * and other components that need role→{id,name} lookups.
 *
 * The BE stores discrete staff fields (photographerId/Name, etc.); this
 * type bridges them to a map keyed by role.
 */
export type AssignedStaffMap = {
  photo?: { id: string; name: string };
  makeup?: { id: string; name: string };
  retouch?: { id: string; name: string };
  sale?: { id: string; name: string };
};

/**
 * Build an AssignedStaffMap from an order's ekip arrays. Mỗi vai lấy phần tử
 * [0] của mảng làm người CHÍNH — 6 cột phẳng cũ (photographerId/Name,
 * makeupId/Name, retoucher/retoucherName) đã bỏ khỏi OpenAPI (migration 0194
 * DROP ở DB), mảng JSONB (photographers/makeups/retouchers) là nguồn duy
 * nhất. `sale` không có mảng tương ứng nên vẫn đọc cặp id/tên đơn lẻ.
 *
 * Usage:
 *   const staff = assignedStaffFromOrder(order);
 *   staff.photo?.name  // "Nguyễn Văn A"
 */
export function assignedStaffFromOrder(o: {
  photographers?: { id: string; name: string }[] | null;
  makeups?: { id: string; name: string }[] | null;
  retouchers?: { id: string; name: string }[] | null;
  saleId?: string | null;
  saleName?: string | null;
}): AssignedStaffMap {
  const out: AssignedStaffMap = {};
  const primary = o.photographers?.[0];
  if (primary?.id) out.photo = { id: primary.id, name: primary.name };
  const makeupPrimary = o.makeups?.[0];
  if (makeupPrimary?.id) out.makeup = { id: makeupPrimary.id, name: makeupPrimary.name };
  const retouchPrimary = o.retouchers?.[0];
  if (retouchPrimary?.id) out.retouch = { id: retouchPrimary.id, name: retouchPrimary.name };
  if (o.saleId && o.saleName)
    out.sale = { id: o.saleId, name: o.saleName };
  return out;
}

/**
 * Return the selected concept string for an order, or null when absent.
 *
 * The BE stores `selectedConcept` (nullable string) and `concept2` (bool).
 * This helper normalises access so components don't need to know the exact
 * field name.
 */
export function selectedConceptOf(o: {
  selectedConcept?: string | null;
  concept2?: boolean;
}): string | null {
  return o.selectedConcept ?? null;
}
